import { formatDateIn, formatNumberIn, toLocale } from '@zuruflex/i18n';
import {
  HANDOVER_PHOTO_KINDS,
  handoverItemKey,
  type BookingHandover,
  type FuelLevel,
  type HandoverPhotoKind,
} from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Skeleton,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import {
  DAMAGE_CHECK,
  FUEL_KEY,
  HANDOVER_PHOTO_LABEL,
  FuelField,
  HandoverPhotoGrid,
  OdometerField,
  SectionLabel,
  TickRow,
  describeFailure,
  photosFromRecord,
  uploadHandoverPhoto,
  useHandoverPhotos,
  useKm,
  type HandoverBooking,
} from './handover-fields.js';

export interface ReturnDialogProps {
  reference: string;
  onClose: () => void;
}

/**
 * Checking a car back in, from the portal.
 *
 * The second half of `HandoverDialog`, and read against it: the reading
 * cannot be below the one the car went out on, the gauge is shown beside
 * the mark it left at, and every item that went out has to be accounted
 * for. What the phone's return screen also does — pricing the overage and
 * the payout while a thumb is on the keypad — stays on the phone for now;
 * the record is the part that cannot be reconstructed later, and the
 * settlement can.
 */
export function ReturnDialog({ reference, onClose }: ReturnDialogProps) {
  const { t } = useTranslation(['partner', 'common']);

  const booking = useQuery({
    queryKey: ['booking', reference],
    queryFn: () => api.getBooking(reference) as Promise<HandoverBooking>,
  });

  const handovers = useQuery({
    queryKey: ['handovers', reference],
    queryFn: () => api.listBookingHandovers(reference),
  });

  const failure = booking.error ?? handovers.error;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-2xl">
        {failure ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('handover.app.return.title')}</DialogTitle>
            </DialogHeader>
            <ErrorState
              error={failure}
              onRetry={() => {
                void booking.refetch();
                void handovers.refetch();
              }}
            />
          </>
        ) : booking.data && handovers.data ? (
          // Mounted once both have landed, for the reason `HandoverDialog`
          // gives: seeded in initialisers, never wiped by a refetch.
          <ReturnForm
            booking={booking.data}
            pickup={handovers.data.items.find((item) => item.kind === 'PICKUP')}
            record={handovers.data.items.find((item) => item.kind === 'RETURN')}
            onClose={onClose}
          />
        ) : (
          <div className="space-y-4" aria-busy>
            <DialogHeader>
              <DialogTitle>{t('handover.app.return.title')}</DialogTitle>
              <DialogDescription>{t('common:state.loading')}</DialogDescription>
            </DialogHeader>
            <Skeleton className="h-28 rounded-card" />
            <Skeleton className="h-40 rounded-card" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReturnForm({
  booking,
  pickup,
  record,
  onClose,
}: {
  booking: HandoverBooking;
  pickup: BookingHandover | undefined;
  /** An unfinished return, written by a commit that did not get to the end. */
  record: BookingHandover | undefined;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const km = useKm();
  const reference = booking.reference;
  const vehicle = booking.vehicle ?? null;

  /*
   * Exactly what went out — no more, no less. The API refuses a return
   * that leaves one of these off or names one that never left
   * (`handover.itemsMismatch`), so the list is the pick-up's, never the
   * car's current one, which may have been edited since.
   */
  const wentOut = (pickup?.items ?? []).filter((item) => item.present);

  /*
   * Unticked by default, the opposite of the pick-up. Here the tick is a
   * claim — "I have seen it" — and a list that arrived pre-ticked would be
   * waved through by a partner in a hurry, which is how a child seat goes
   * missing without anybody having said so.
   */
  const [back, setBack] = useState<Set<string>>(
    () =>
      new Set(
        (record?.items ?? [])
          .filter((item) => item.present)
          .map((item) => handoverItemKey(item.label)),
      ),
  );

  const [odometer, setOdometer] = useState(record ? String(record.odometerKm) : '');
  const [fuel, setFuel] = useState<FuelLevel | null>(record?.fuelLevel ?? null);
  /*
   * Seeded from the record as well, and it has to be: the commit always
   * posts the checklist and the API's upsert replaces the column with it,
   * so a correction that only fixed the reading would otherwise clear a
   * damage flag somebody ticked — the one field a deposit dispute turns on.
   */
  const [damage, setDamage] = useState(record?.checklist.includes(DAMAGE_CHECK) ?? false);
  const { photos, pick, markFiled, complete } = useHandoverPhotos(photosFromRecord(record));
  const [uploading, setUploading] = useState<HandoverPhotoKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const guest = booking.customer?.fullName ?? t('bookings.unnamedCustomer');
  const car = vehicle ? `${vehicle.make} ${vehicle.model}` : t('common:field.vehicle');

  const reading = odometer === '' ? null : Number(odometer);
  const backwards = reading !== null && pickup !== undefined && reading < pickup.odometerKm;
  const driven = reading !== null && pickup && !backwards ? reading - pickup.odometerKm : null;
  const missing = wentOut.filter((item) => !back.has(handoverItemKey(item.label)));

  /*
   * The four angles are required here as they are at the pick-up — "the
   * same four on the way out and on the way back", as `HandoverPhotoKind`
   * puts it — because a pair is the only reason to photograph a car at
   * all. The items never gate it: a seat that did not come back is a fact
   * to record, not a reason the car cannot be checked in.
   */
  const ready = Boolean(pickup) && reading !== null && !backwards && fuel !== null && complete;

  const commit = useMutation({
    mutationFn: async () => {
      if (reading === null || fuel === null) return;

      // An upsert, like the pick-up: pressing the button again corrects.
      await api.recordBookingHandover(reference, {
        kind: 'RETURN',
        odometerKm: reading,
        fuelLevel: fuel,
        checklist: damage ? [DAMAGE_CHECK] : [],
        items: wentOut.map((item) => ({
          label: item.label,
          present: back.has(handoverItemKey(item.label)),
        })),
      });

      for (const photoKind of HANDOVER_PHOTO_KINDS) {
        const slot = photos[photoKind];
        if (!slot?.file || slot.filed) continue;
        setUploading(photoKind);
        await uploadHandoverPhoto(
          reference,
          slot.file,
          { kind: 'RETURN', photoKind },
          t('handover.web.uploadFailed', { angle: t(HANDOVER_PHOTO_LABEL[photoKind]) }),
        );
        markFiled(photoKind);
      }
      setUploading(null);

      // Last, because it seals both the record and the photographs.
      await api.updateBookingStatus(reference, 'COMPLETED');
    },
    onMutate: () => setError(null),
    onError: (cause) => {
      setUploading(null);
      setError(
        `${describeFailure(cause, t('common:error.unexpected'))} ${t('handover.web.partWayReturn')}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['handovers', reference] });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['booking', reference] }),
        queryClient.invalidateQueries({ queryKey: ['handovers', reference] }),
        // The finished hire now owes a guest review.
        queryClient.invalidateQueries({ queryKey: ['guests-to-review'] }),
        queryClient.invalidateQueries({ queryKey: ['my-fleet'] }),
      ]);
      onClose();
    },
  });

  const busy = commit.isPending;

  const toggle = (label: string, present: boolean) =>
    setBack((current) => {
      const next = new Set(current);
      if (present) next.add(handoverItemKey(label));
      else next.delete(handoverItemKey(label));
      return next;
    });

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) commit.mutate();
      }}
    >
      <DialogHeader>
        <DialogTitle>{t('handover.web.returnTitle', { car })}</DialogTitle>
        <DialogDescription>
          {t('handover.web.returnDescription', {
            guest,
            plate: vehicle?.plateNumber ?? '',
            reference,
          })}
        </DialogDescription>
      </DialogHeader>

      {/*
        The API refuses a return with no pick-up behind it, so say so up
        front rather than after the partner has photographed the car.
      */}
      {!pickup ? <Alert variant="warning">{t('errors:handover.pickupFirst')}</Alert> : null}
      {record ? <Alert variant="info">{t('handover.web.resumedReturn')}</Alert> : null}

      <div className="grid gap-5 sm:grid-cols-2">
        <OdometerField
          value={odometer}
          onChange={setOdometer}
          disabled={busy || !pickup}
          error={
            backwards && pickup
              ? t('errors:handover.odometerWentBackwards', {
                  pickup: formatNumberIn(pickup.odometerKm, locale),
                  given: formatNumberIn(reading ?? 0, locale),
                })
              : undefined
          }
          hint={
            pickup ? (
              <>
                {t('handover.app.record.odometerLast', {
                  reading: km(pickup.odometerKm),
                  date: formatDateIn(pickup.recordedAt, locale),
                })}
                {driven !== null ? (
                  <>
                    {' '}
                    <span className="font-medium text-text">
                      {t('handover.app.return.driven')} · {km(driven)}
                    </span>
                  </>
                ) : null}
              </>
            ) : undefined
          }
        />
        <FuelField
          value={fuel}
          onChange={setFuel}
          disabled={busy || !pickup}
          hint={
            pickup
              ? t('handover.web.fuelOut', {
                  level: t(`handover.fuel.level.${FUEL_KEY[pickup.fuelLevel]}`),
                })
              : undefined
          }
        />
      </div>

      <section>
        <SectionLabel>{t('handover.web.cameBack')}</SectionLabel>
        {wentOut.length > 0 ? (
          <>
            <p className="mt-1 text-xs text-text-muted">{t('handover.web.cameBackHint')}</p>
            <div className="mt-1">
              {wentOut.map((item) => {
                const isBack = back.has(handoverItemKey(item.label));
                return (
                  <TickRow
                    key={item.label}
                    checked={isBack}
                    disabled={busy}
                    onChange={(next) => toggle(item.label, next)}
                    title={item.label}
                    aside={
                      isBack ? null : (
                        <Badge variant="warning" className="shrink-0">
                          {t('handover.web.notBack')}
                        </Badge>
                      )
                    }
                  />
                );
              })}
            </div>
            {missing.length > 0 ? (
              <p className="mt-2 text-xs text-warning">
                {t('handover.web.missingSummary', {
                  items: missing.map((item) => item.label).join(', '),
                })}
              </p>
            ) : null}
          </>
        ) : (
          <p className="mt-2 text-sm text-text-muted">{t('handover.web.nothingWentOut')}</p>
        )}
      </section>

      <HandoverPhotoGrid
        photos={photos}
        onPick={pick}
        busyKind={uploading}
        disabled={busy || !pickup}
        onRejected={setError}
      />

      <section>
        <TickRow
          checked={damage}
          disabled={busy || !pickup}
          onChange={setDamage}
          title={t('handover.app.return.newDamage')}
          body={t('handover.app.return.newDamageHint')}
        />
      </section>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <DialogFooter className="items-center">
        {!ready && pickup ? (
          <p className="mr-auto text-xs text-text-muted">{t('handover.web.notReadyReturn')}</p>
        ) : null}
        <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
          {t('common:action.cancel')}
        </Button>
        <Button type="submit" disabled={!ready} isLoading={busy}>
          {t('handover.app.return.finish')}
        </Button>
      </DialogFooter>
    </form>
  );
}
